export const MCP_DOMAIN_MENTIONS_MARKER_PREFIX = '__mcp_domains__:';

const DOMAIN_MENTIONS_MARKER_RE = /__mcp_domains__:(\[[^\n]*\])/;

export type DomainMention = {
  slug: string;
  url: string;
  show_name?: string | null;
};

/** Badge / list label: `address (display name)` when show_name exists. */
export function domainMentionLabel(domain: {
  url?: string | null;
  show_name?: string | null;
  name?: string | null;
}): string {
  const address = domain.url?.trim() || domain.name?.trim() || 'دامنه';
  const showName = domain.show_name?.trim();
  if (showName) {
    return `${address} (${showName})`;
  }
  return address;
}

export function buildDomainMentionsMarker(domains: DomainMention[]): string {
  const payload = domains.map(d => ({
    slug: d.slug,
    url: d.url,
    ...(d.show_name?.trim() ? { show_name: d.show_name.trim() } : {})
  }));
  return `${MCP_DOMAIN_MENTIONS_MARKER_PREFIX}${JSON.stringify(payload)}`;
}

/** Visible line(s) + hidden marker so the model/tools can resolve slugs. */
export function appendDomainMentionsToMessage(
  text: string,
  domains: DomainMention[]
): string {
  if (!domains.length) return text;
  const labels = domains.map(d => domainMentionLabel(d)).join('، ');
  const visibleLine = `دامنه: ${labels}`;
  const marker = buildDomainMentionsMarker(domains);
  const body = text.replace(/\u200b/g, '').trim();
  if (!body) return `${visibleLine}\n${marker}`;
  return `${visibleLine}\n${body}\n${marker}`;
}

export function parseDomainMentionsMarker(
  content: string | null | undefined
): DomainMention[] | null {
  if (!content) return null;
  const match = content.match(DOMAIN_MENTIONS_MARKER_RE);
  const raw = match?.[1]?.trim();
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const out: DomainMention[] = [];
    for (const item of parsed) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
      const row = item as Record<string, unknown>;
      const slug = typeof row.slug === 'string' ? row.slug.trim() : '';
      const url = typeof row.url === 'string' ? row.url.trim() : '';
      if (!slug || !url) continue;
      const show_name =
        typeof row.show_name === 'string' ? row.show_name.trim() : null;
      out.push({ slug, url, show_name });
    }
    return out.length > 0 ? out : null;
  } catch {
    return null;
  }
}

/** Strip machine marker from user-visible message text. */
export function displayDomainMentionUserMessage(
  content: string | null | undefined
): string {
  if (!content) return '';
  return content
    .replace(DOMAIN_MENTIONS_MARKER_RE, '')
    .replace(/\n{2,}/g, '\n')
    .trim();
}
